import type {
  EquityChangeData,
  TrialPaymentReminderData,
  UserCreditUpdateData,
} from './types';
import { USER_POINTS_STATUS_WARNING } from '@const/index';
import globalEventEmitter from '@modules/EventEmitter';
import { EVENTS } from '@modules/EventEmitter/const';
import NotificationSocket from '@services/socket/NotificationSocket';
import { useTrialPaymentReminderStore } from '@stores/trialPaymentReminder';
import { useUserStore } from '@stores/user';
import { PAYMENT_EVENTS } from './const';

/**
 * 支付 Socket
 */
class PaymentSocket {
  private static instance: PaymentSocket;
  private currentUserId?: string;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): PaymentSocket {
    if (!PaymentSocket.instance) {
      PaymentSocket.instance = new PaymentSocket();
    }
    return PaymentSocket.instance;
  }

  private get socket() {
    return NotificationSocket.getSocket();
  }

  initialize(userId: string): void {
    if (this.isInitialized && this.currentUserId === userId) {
      return;
    }

    if (this.isInitialized && this.currentUserId !== userId) {
      this.disconnect();
    }

    this.currentUserId = userId;
    NotificationSocket.initialize(userId);
    this.setupEventListeners();
    NotificationSocket.joinRoom(userId);
    this.isInitialized = true;
  }

  private setupEventListeners(): void {
    this.socket?.on(PAYMENT_EVENTS.USER_CREDIT_UPDATE, this.handleCreditUpdate);
    this.socket?.on(PAYMENT_EVENTS.EQUITY_CHANGE_FINISH, this.handleEquityChange);
    this.socket?.on(PAYMENT_EVENTS.TRIAL_PACKAGE_PAYMENT_REMINDER, this.handleTrialPaymentReminder);
  }

  /**
   * 积分更新
   * @param data 积分更新数据
   */
  private handleCreditUpdate = (data: UserCreditUpdateData): void => {
    if (data?.metadata?.creditCount === undefined) {
      return;
    }

    const { grant } = data.data.currUserProperty;
    const totalEffective = data.metadata.creditCount;
    const userStore = useUserStore.getState();

    userStore.setUserPoints(totalEffective);

    if (
      userStore.userPointsStatus !== 'warning' &&
      totalEffective <= USER_POINTS_STATUS_WARNING &&
      !grant
    ) {
      userStore.setUserPointsStatus('warning');
      userStore.setIsFirstChangePointsStatus(true);
    }

    globalEventEmitter.emit(EVENTS.PAYMENT_CREDIT_UPDATE, totalEffective);
  };

  /**
   * 权益变更完成
   * @param data 权益变更完成数据
   */
  private handleEquityChange = (data: EquityChangeData): void => {
    const messageData = data?.data;
    if (!messageData) {
      return;
    }

    useUserStore.getState().setPaymentStatus(messageData);

    if (messageData.success) {
      globalEventEmitter.emit(EVENTS.PAYMENT_SUCCESS, messageData);
    }
  };

  /**
   * 试用套餐支付提醒
   * @param data 试用套餐支付提醒数据
   */
  private handleTrialPaymentReminder = (data: TrialPaymentReminderData): void => {
    const messageData = data?.data;
    if (!messageData?.projectId || !messageData?.sessionId) {
      return;
    }

    const { projectId, sessionId, messageId } = messageData;

    if (messageId) {
      useTrialPaymentReminderStore.getState().setAllowed(projectId, messageId);
    }

    globalEventEmitter.emit(EVENTS.TRIAL_PACKAGE_PAYMENT_REMINDER, {
      projectId,
      sessionId,
    });
  };

  disconnect(): void {
    if (this.currentUserId) {
      NotificationSocket.leaveRoom(this.currentUserId);
    }
    this.socket?.off(PAYMENT_EVENTS.USER_CREDIT_UPDATE, this.handleCreditUpdate);
    this.socket?.off(PAYMENT_EVENTS.EQUITY_CHANGE_FINISH, this.handleEquityChange);
    this.socket?.off(PAYMENT_EVENTS.TRIAL_PACKAGE_PAYMENT_REMINDER, this.handleTrialPaymentReminder);
    this.currentUserId = undefined;
    this.isInitialized = false;
  }

  get userId(): string | undefined {
    return this.currentUserId;
  }
}

export default PaymentSocket.getInstance();
